import { i18n } from 'boot/i18n';
import { formatCalendarDate } from 'src/utils/DateUtils';
import { computed, onMounted, ref } from 'vue';

import { HOLD_SUBJECT, parseSessionPlanDeclaration } from '../../domain/constants/holdTaskTypes';
import {
  clientName,
  currentClinicId,
  ensureTreatmentServicesBound,
  sessionDateOf,
  staffMemberForLogin,
  treatmentServiceName,
} from '../../infrastructure/api/holdSubjectApi';
import { createLookup } from '../../infrastructure/api/lookup';
import { useFundingSourceVersions } from './useFundingSourceVersions';

/**
 * Plan names, kept for the session like the session dates, so a plan is read once however many pages
 * name it. The plan is not billing's, so the host says how to read one; every host reads it the same
 * way, which is why the first reader to arrive is the one kept.
 */
let sessionPlanNames = null;
const sessionPlanNamesReadBy = (readSessionPlanName) => {
  sessionPlanNames ??= createLookup({ read: readSessionPlanName, label: 'a session plan name for a hold task' });

  return sessionPlanNames;
};

/**
 * The words for what a hold is about: a client's or a payer's name, a staff member, a session, a
 * treatment service, a code.
 *
 * PAYERS ARE LOADED ONCE, every one the clinic has configured, including one that has not started
 * yet: a "Not in effect" task is about exactly such a payer. A session is named by its client and
 * the date it was billed on, looked up when first asked for. Everything else comes from
 * what the app already holds. A name that cannot be found is null, and the caller shows what it has
 * rather than an id.
 *
 * A caller that has already loaded the clinic's payers passes them, and they are not read twice.
 * A session plan is named only where the host says how to read one.
 *
 * @param {{
 *   payers?: import('vue').Ref<{fundingSourceId: string, name: string}[]>,
 *   readSessionPlanName?: ((sessionPlanId: string) => Promise<string|null>)|null,
 * }} [options]
 * @returns {{ nameOf: (subject: string, subjectId: string, clientId?: string|null) => string|null }}
 */
// eslint-disable-next-line import/prefer-default-export
export function useHoldSubjectNames({ payers: loadedPayers = null, readSessionPlanName = null } = {}) {
  const planNames = readSessionPlanName ? sessionPlanNamesReadBy(readSessionPlanName) : null;
  const ownPayers = ref([]);
  const funders = useFundingSourceVersions();
  const payerNames = computed(() => new Map(((loadedPayers ?? ownPayers).value ?? [])
    .map((payer) => [payer.fundingSourceId, payer.name])));

  onMounted(async () => {
    const clinicId = currentClinicId();

    if (!clinicId) {
      return;
    }
    // Each on its own: payers that loaded still name their tasks when the services do not.
    const [loadedResult, servicesResult] = await Promise.allSettled([
      loadedPayers ? null : funders.loadConfigured({ clinicId }),
      ensureTreatmentServicesBound(),
    ]);

    if (loadedResult.status === 'fulfilled') {
      ownPayers.value = loadedResult.value ?? [];
    }
    [loadedResult, servicesResult].filter(({ status }) => status === 'rejected').forEach(({ reason }) => {
      // Names only: a task still reads, with its count and without whose it is.
      console.error('Failed to load names for hold tasks', reason);
    });
  });

  const sessionName = (sessionActivityId, clientId) => {
    const clinicId = currentClinicId();
    // Not before the clinic is known: a read without one finds nothing.
    const date = clinicId ? sessionDateOf(clinicId, sessionActivityId) : null;
    const parts = [clientId ? clientName(clientId) : null, date ? formatCalendarDate(date) : null];

    return parts.filter(Boolean).join(' · ') || null;
  };

  // The type only when it is not the usual one: a plan's supervision is a different service.
  const sessionPlanName = (subjectId, clientId) => {
    const declaration = parseSessionPlanDeclaration(subjectId);

    if (!declaration) {
      return null;
    }
    const parts = [
      clientId ? clientName(clientId) : null,
      planNames?.get(declaration.sessionPlanId),
      declaration.activityType === 'session'
        ? null
        : i18n.t(`sessions.modals.sessionActivity.types.${declaration.activityType}`),
    ];

    return parts.filter(Boolean).join(' · ') || null;
  };

  const nameOf = (subject, subjectId, clientId = null) => {
    switch (subject) {
      case HOLD_SUBJECT.CLIENT:
        return clientName(subjectId);
      case HOLD_SUBJECT.SESSION:
        return sessionName(subjectId, clientId);
      case HOLD_SUBJECT.SESSION_PLAN:
        return sessionPlanName(subjectId, clientId);
      case HOLD_SUBJECT.STAFF:
        return staffMemberForLogin(subjectId)?.clientName ?? null;
      case HOLD_SUBJECT.PAYER:
        return payerNames.value.get(subjectId) ?? null;
      case HOLD_SUBJECT.TREATMENT_SERVICE:
        return treatmentServiceName(subjectId);
      case HOLD_SUBJECT.CODE:
        return subjectId;
      default:
        return null;
    }
  };

  return { nameOf };
} // useHoldSubjectNames()
